import React, { useState, useEffect } from 'react';
import { Button, Typography, Checkbox, IconButton, Spinner, Dialog, DialogHeader, DialogBody } from "@material-tailwind/react";
import { TrashIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useLanguage } from '../contexts/LanguageContext';
import { languages } from '../languages';
import { mcDePageClass } from '../utils/branding';
import axios from 'axios';

const ShoppingList = () => {
    const { language } = useLanguage();

    let translationLanguage = language;
    if (language === 'befr' || language === 'be-fr') translationLanguage = 'french';
    else if (language === 'benl' || language === 'be-nl') translationLanguage = 'nederlands';
    else if (language === 'cyprus' || language === 'cy' || language === 'el-cy') translationLanguage = 'greek';

    const t = languages[translationLanguage] || languages['english'];
    const [items, setItems] = useState([]);
    const [newItem, setNewItem] = useState('');
    const [newQuantity, setNewQuantity] = useState('');
    const [priceData, setPriceData] = useState(null);
    const [loadingPrices, setLoadingPrices] = useState(false);
    const [userCountry, setUserCountry] = useState(null);
    const [showPrices, setShowPrices] = useState(false);
    const [showClearConfirm, setShowClearConfirm] = useState(false);

    const normalizeItem = (item) => {
        if (item.name && typeof item.name === 'object' && item.name.name) {
            return { name: item.name.name, quantity: item.name.quantity || '', checked: !!item.checked };
        }
        if (typeof item.name === 'string') {
            return { name: item.name, quantity: item.quantity || '', checked: !!item.checked };
        }
        return { name: String(item.name || ''), quantity: item.quantity || '', checked: !!item.checked };
    };

    useEffect(() => {
        const loadItems = () => {
            const savedItems = localStorage.getItem('shoppingList');
            if (savedItems) {
                try {
                    const parsedItems = JSON.parse(savedItems);
                    const normalized = parsedItems.map(normalizeItem);
                    setItems(normalized);
                    localStorage.setItem('shoppingList', JSON.stringify(normalized));
                } catch (error) {
                    console.error('Error parsing saved items:', error);
                }
            }
        };

        loadItems();
        window.addEventListener('storage', loadItems);
        return () => window.removeEventListener('storage', loadItems);
    }, []);

    useEffect(() => {
        const detectCountry = async () => {
            try {
                const response = await fetch('https://ipapi.co/json/');
                const data = await response.json();
                if (data.country_code) {
                    setUserCountry(data.country_code.toUpperCase());
                    return;
                }
            } catch { /* fallback below */ }

            try {
                const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const timezoneMap = {
                    'Europe/Amsterdam': 'NL', 'Europe/Brussels': 'BE', 'Europe/Berlin': 'DE',
                    'Europe/Paris': 'FR', 'Europe/Madrid': 'ES', 'Europe/Rome': 'IT',
                    'Europe/Lisbon': 'PT', 'Europe/London': 'GB', 'Europe/Dublin': 'IE',
                    'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US', 'America/Los_Angeles': 'US',
                    'America/Toronto': 'CA', 'America/Vancouver': 'CA',
                    'America/Mexico_City': 'MX', 'America/Cancun': 'MX',
                    'America/Sao_Paulo': 'BR', 'America/Bahia': 'BR',
                    'America/Bogota': 'CO', 'America/Buenos_Aires': 'AR', 'America/Argentina/Buenos_Aires': 'AR',
                    'America/Santiago': 'CL', 'America/Lima': 'PE',
                    'Europe/Oslo': 'NO', 'Europe/Stockholm': 'SE',
                    'Europe/Copenhagen': 'DK', 'Europe/Warsaw': 'PL', 'Europe/Vienna': 'AT',
                    'Europe/Zurich': 'CH', 'Europe/Bucharest': 'RO', 'Europe/Belgrade': 'RS',
                    'Europe/Ljubljana': 'SI', 'Europe/Zagreb': 'HR',
                    'Europe/Athens': 'GR', 'Europe/Prague': 'CZ', 'Europe/Budapest': 'HU',
                    'Europe/Bratislava': 'SK', 'Europe/Sofia': 'BG', 'Europe/Istanbul': 'TR',
                    'Europe/Helsinki': 'FI',
                    'Asia/Dubai': 'AE', 'Asia/Bahrain': 'BH', 'Asia/Riyadh': 'SA',
                    'Asia/Kuwait': 'KW', 'Asia/Qatar': 'QA', 'Asia/Muscat': 'OM',
                    'Asia/Baghdad': 'IQ', 'Asia/Amman': 'JO', 'Asia/Beirut': 'LB',
                    'Asia/Damascus': 'SY', 'Asia/Gaza': 'PS', 'Asia/Hebron': 'PS',
                    'Asia/Aden': 'YE', 'Asia/Jerusalem': 'IL', 'Asia/Tel_Aviv': 'IL',
                    'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN',
                    'Asia/Seoul': 'KR', 'Asia/Singapore': 'SG', 'Asia/Kuala_Lumpur': 'MY',
                    'Asia/Bangkok': 'TH', 'Asia/Manila': 'PH',
                    'Africa/Cairo': 'EG', 'Africa/Johannesburg': 'ZA', 'Africa/Lagos': 'NG',
                    'Africa/Nairobi': 'KE', 'Africa/Casablanca': 'MA', 'Africa/Tunis': 'TN',
                    'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU', 'Australia/Perth': 'AU',
                    'Pacific/Auckland': 'NZ',
                    'Asia/Tokyo': 'JP',
                };
                if (timezoneMap[timezone]) setUserCountry(timezoneMap[timezone]);
            } catch { /* silent */ }
        };
        detectCountry();
    }, []);

    // Override country based on selected language (language implies target country)
    useEffect(() => {
        const languageCountryMap = {
            'mx-es': 'MX',
            'nederlands': 'NL', 'benl': 'NL', 'be-nl': 'NL',
            'deutsch': 'DE',
            'spanish': 'ES',
            'french': 'FR', 'befr': 'FR', 'be-fr': 'FR',
            'portuguese': 'PT',
            'english': 'GB',
            'polish': 'PL',
            'romanian': 'RO',
            'serbian': 'RS',
            'norwegian': 'NO',
            'danish': 'DK',
            'swedish': 'SE',
            'slovenian': 'SI',
            'arabic': 'SA',
            'bahrain-arabic': 'BH', 'bahrain-english': 'BH',
            'uae-arabic': 'AE', 'uae-english': 'AE',
            'sa-arabic': 'SA', 'sa-english': 'SA',
            'kw-arabic': 'KW', 'kw-english': 'KW',
            'qa-arabic': 'QA', 'qa-english': 'QA',
            'om-arabic': 'OM', 'om-english': 'OM',
            'iq-arabic': 'IQ', 'iq-english': 'IQ',
            'jo-arabic': 'JO', 'jo-english': 'JO',
            'lb-arabic': 'LB', 'lb-english': 'LB',
            'sy-arabic': 'SY', 'sy-english': 'SY',
            'ps-arabic': 'PS', 'ps-english': 'PS',
            'ye-arabic': 'YE', 'ye-english': 'YE',
            'eg-arabic': 'EG', 'eg-english': 'EG',
        };
        const mapped = languageCountryMap[language];
        if (mapped) {
            setUserCountry(mapped);
            setPriceData(null);
        }
    }, [language]);

    useEffect(() => {
        if (items.length > 0) {
            localStorage.setItem('shoppingList', JSON.stringify(items));
        }
    }, [items]);

    // Reset price data when items change
    useEffect(() => {
        setPriceData(null);
        setShowPrices(false);
    }, [items.length]);

    const addItem = () => {
        if (newItem.trim() !== '') {
            const newItemObject = { name: newItem.trim(), quantity: newQuantity.trim() || '1', checked: false };
            setItems(prevItems => {
                const updatedItems = [...prevItems, newItemObject];
                localStorage.setItem('shoppingList', JSON.stringify(updatedItems));
                return updatedItems;
            });
            setNewItem('');
            setNewQuantity('');
        }
    };

    const removeItem = (index) => {
        setItems(prevItems => {
            const newItems = prevItems.filter((_, i) => i !== index);
            localStorage.setItem('shoppingList', JSON.stringify(newItems));
            return newItems;
        });
    };

    const toggleItem = (index) => {
        setItems(prevItems => {
            const newItems = prevItems.map((item, i) =>
                i === index ? { ...item, checked: !item.checked } : item
            );
            localStorage.setItem('shoppingList', JSON.stringify(newItems));
            return newItems;
        });
    };

    const clearList = () => {
        setItems([]);
        localStorage.removeItem('shoppingList');
        setPriceData(null);
        setShowPrices(false);
    };

    const handleComparePrices = async () => {
        if (priceData) {
            setShowPrices(!showPrices);
            return;
        }

        setLoadingPrices(true);
        try {
            const uncheckedItems = items.filter(i => !i.checked);
            if (uncheckedItems.length === 0) return;

            const response = await axios.post('/api/ai/shopping-prices', {
                items: uncheckedItems.map(i => ({ name: i.name, quantity: i.quantity })),
                country: userCountry || 'NL',
                language: translationLanguage || 'english',
            }, { timeout: 60000 });

            setPriceData(response.data);
            setShowPrices(true);
        } catch (error) {
            console.error('Error comparing prices:', error);
        } finally {
            setLoadingPrices(false);
        }
    };

    const getRatingColor = (rating) => {
        if (rating === 'budget') return 'bg-green-100 text-green-800';
        if (rating === 'premium') return 'bg-purple-100 text-purple-800';
        return 'bg-blue-100 text-blue-800';
    };

    const getRatingLabel = (rating) => {
        if (rating === 'budget') return t.budget;
        if (rating === 'premium') return t.premium;
        return t.midRange;
    };

    const confirmClear = () => {
        clearList();
        setShowClearConfirm(false);
    };

    return (
        <div className={`p-4 ${mcDePageClass(language)}`}>
            <div className='flex items-center gap-2 justify-between mb-4'>
                <Typography variant='h5'>{t.shoppingList}</Typography>
                {items.length > 0 && (
                    <Typography variant="small" className="text-gray-400">
                        {items.filter(i => !i.checked).length} / {items.length}
                    </Typography>
                )}
            </div>

            <div className="mb-4">
                <div className='flex items-start gap-2 mb-2'>
                    <input
                        type="text"
                        placeholder={t.addItem}
                        value={newItem}
                        onChange={(e) => setNewItem(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addItem()}
                        className="bg-white w-2/3 p-2 rounded-md border"
                    />
                    <input
                        type="text"
                        placeholder={t.quantity}
                        value={newQuantity}
                        onChange={(e) => setNewQuantity(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addItem()}
                        className="bg-white w-1/3 p-2 rounded-md border"
                    />
                </div>
                <Button color="green" onClick={addItem} className="flex items-center justify-center gap-2 w-full">
                    <PlusIcon className="h-4 w-4" />
                    {t.add}
                </Button>
            </div>

            {/* Action Buttons */}
            {items.length > 0 && (
                <div className="flex gap-2 mb-4">
                    <Button
                        variant="outlined"
                        color="blue"
                        size="sm"
                        onClick={handleComparePrices}
                        disabled={loadingPrices || items.filter(i => !i.checked).length === 0}
                        className="flex-1 flex items-center justify-center gap-2 rounded-lg normal-case text-sm"
                    >
                        {loadingPrices ? (
                            <Spinner className="h-4 w-4" />
                        ) : (
                            <span>🏪</span>
                        )}
                        {loadingPrices ? t.comparingPrices : t.comparePrices}
                    </Button>
                    <Button
                        variant="outlined"
                        color="red"
                        size="sm"
                        onClick={() => setShowClearConfirm(true)}
                        className="flex items-center justify-center gap-2 rounded-lg normal-case text-sm"
                    >
                        <TrashIcon className="h-4 w-4" />
                        {t.clearList}
                    </Button>
                </div>
            )}

            {/* Clear Confirmation Dialog */}
            <Dialog open={showClearConfirm} handler={() => setShowClearConfirm(false)} size="xs">
                <DialogBody className="text-center py-8 px-6">
                    <div className="text-4xl mb-4">🗑️</div>
                    <Typography variant="h5" className="mb-2">
                        {t.clearListConfirmTitle || 'Clear shopping list?'}
                    </Typography>
                    <Typography variant="small" className="text-gray-600 mb-6 block">
                        {t.clearListConfirmText || 'This will remove all items from your list. This cannot be undone.'}
                    </Typography>
                    <div className="flex gap-3 justify-center">
                        <Button variant="outlined" color="gray" onClick={() => setShowClearConfirm(false)} className="normal-case">
                            {t.cancel}
                        </Button>
                        <Button color="red" onClick={confirmClear} className="normal-case">
                            {t.clearList}
                        </Button>
                    </div>
                </DialogBody>
            </Dialog>

            <ul className="space-y-2">
                {items.map((item, index) => (
                    <li key={index} className="flex items-center justify-between bg-gray-100 p-2 rounded">
                        <div className="flex items-center">
                            <Checkbox
                                color="green"
                                checked={item.checked}
                                onChange={() => toggleItem(index)}
                            />
                            <span onClick={() => toggleItem(index)} className={`cursor-pointer ${item.checked ? 'line-through text-gray-400' : ''}`}>
                                {item.quantity ? `${item.name} (${item.quantity})` : item.name}
                            </span>
                        </div>
                        <IconButton color="red" variant="text" onClick={() => removeItem(index)}>
                            <TrashIcon className="h-5 w-5" />
                        </IconButton>
                    </li>
                ))}
            </ul>

            {/* Price Comparison Dialog */}
            <Dialog
                open={showPrices && !!priceData}
                handler={() => setShowPrices(false)}
                size="lg"
                className="max-h-[90vh] overflow-hidden flex flex-col"
            >
                <DialogHeader className="flex items-center justify-between px-4 py-3 border-b shrink-0">
                    <Typography variant="h5" className="flex items-center gap-2">
                        <span>💰</span> {t.priceComparison}
                    </Typography>
                    <div className="flex items-center gap-2">
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-500">
                            AI {t.estimate} · 📍 {userCountry}
                        </span>
                        <IconButton variant="text" size="sm" onClick={() => setShowPrices(false)}>
                            <XMarkIcon className="h-5 w-5" />
                        </IconButton>
                    </div>
                </DialogHeader>
                <DialogBody className="overflow-y-auto px-4 py-4 space-y-4">
                    {priceData && (
                        <>
                            {/* Savings Banner */}
                            {priceData.potential_savings > 0 && (
                                <div className="bg-green-100 rounded-lg p-3">
                                    <Typography variant="small" className="text-green-800 font-bold">
                                        💡 {t.potentialSavings}: {priceData.currencySymbol}{priceData.potential_savings.toFixed(2)}
                                    </Typography>
                                    <Typography variant="small" className="text-green-700">
                                        {t.cheapestAt}: <strong>{priceData.cheapest}</strong>
                                    </Typography>
                                </div>
                            )}

                            {priceData.tip && (
                                <div className="bg-yellow-50 rounded-lg p-2 border border-yellow-200">
                                    <Typography variant="small" className="text-yellow-800">
                                        💡 {priceData.tip}
                                    </Typography>
                                </div>
                            )}

                            {/* Supermarket Cards */}
                            <div className="space-y-3">
                                {priceData.supermarkets
                                    ?.sort((a, b) => a.total - b.total)
                                    .map((store, idx) => (
                                        <div
                                            key={idx}
                                            className={`rounded-xl border-2 overflow-hidden transition-all ${
                                                store.name === priceData.cheapest
                                                    ? 'border-green-400 shadow-lg'
                                                    : 'border-gray-200'
                                            }`}
                                        >
                                            <div className={`px-4 py-3 flex items-center justify-between ${
                                                store.name === priceData.cheapest ? 'bg-green-50' : 'bg-gray-50'
                                            }`}>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-2xl">{store.logo_emoji || '🏪'}</span>
                                                    <div>
                                                        <Typography variant="h6" className="text-sm font-bold">
                                                            {store.name}
                                                            {store.name === priceData.cheapest && (
                                                                <span className="ml-2 text-green-600 text-xs">✅ {t.cheapest}</span>
                                                            )}
                                                        </Typography>
                                                        <span className={`text-xs px-2 py-0.5 rounded-full ${getRatingColor(store.rating)}`}>
                                                            {getRatingLabel(store.rating)}
                                                        </span>
                                                    </div>
                                                </div>
                                                <Typography variant="h5" className={`font-bold ${
                                                    store.name === priceData.cheapest ? 'text-green-600' : 'text-gray-800'
                                                }`}>
                                                    {priceData.currencySymbol}{store.total?.toFixed(2)}
                                                </Typography>
                                            </div>
                                            <div className="px-4 py-2 divide-y divide-gray-100">
                                                {store.items?.map((item, itemIdx) => (
                                                    <div key={itemIdx} className="flex justify-between py-1.5 text-sm">
                                                        <span className="text-gray-700">{item.name}</span>
                                                        <span className="text-gray-900 font-medium whitespace-nowrap ml-2">
                                                            {priceData.currencySymbol}{item.price?.toFixed(2)}
                                                            {item.unit && <span className="text-gray-400 text-xs ml-1">/{item.unit}</span>}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                            </div>

                            <Typography variant="small" className="text-center text-gray-400 text-xs">
                                ⚠️ {t.priceDisclaimer}
                            </Typography>
                        </>
                    )}
                </DialogBody>
            </Dialog>
        </div>
    );
};

export default ShoppingList;
